"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useRef } from "react";

export interface CareerProspects3Data {
  id?: string | number;
  status?: boolean;
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  background_color?: string;
  certificate_image?: string;
  certificate_image_alt?: string;
}

interface CareerProspects3Props {
  data: CareerProspects3Data | { data: CareerProspects3Data };
}

function resolvePayload(
  props: CareerProspects3Props["data"],
): CareerProspects3Data | undefined {
  const anyData = props as { data?: CareerProspects3Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as CareerProspects3Data;
  }
  return props as CareerProspects3Data;
}

const DEFAULT_TITLE = "Probable";
const DEFAULT_SUB = "Tools &\nFrameworks";

function HeadlineCopy({
  title,
  titleColor,
  subTitle,
  subColor,
  className,
}: {
  title: string;
  titleColor: string;
  subTitle?: string;
  subColor: string;
  className?: string;
}) {
  return (
    <h2
      className={cn(
        "m-0 font-semibold leading-[1.1] tracking-tight",
        className,
      )}
    >
      <span style={{ color: titleColor }}>{title}</span>
      {subTitle ? (
        <>
          <br />
          <span
            className="whitespace-pre-line"
            style={{ color: subColor }}
          >
            {subTitle}
          </span>
        </>
      ) : null}
    </h2>
  );
}

function CareerProspects3({ data }: CareerProspects3Props) {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    title = DEFAULT_TITLE,
    title_text_color = "#000000",
    sub_title = DEFAULT_SUB,
    sub_title_text_color = "#D35400",
    background_color = "#FFFFFF",
    certificate_image = "",
    certificate_image_alt = "Tools and frameworks",
  } = sectionData || {};

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "career_prospects_3_section");
  //   }
  // }, []);

  if (status === false) {
    return null;
  }

  const hasImage =
    typeof certificate_image === "string" && certificate_image.trim() !== "";
  const src = hasImage ? certificate_image.trim() : "";

  const sectionStyle = {
    backgroundColor: background_color,
    fontFamily: "Poppins, sans-serif",
  };

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "mx-auto flex w-full items-center justify-center",
      )}
      style={sectionStyle}
    >
      {/* Mobile: stack; headline wrapper centers the block, text-center aligns lines */}
      <div
        className={cn(
          "flex w-full flex-col items-center px-[50px] pt-[60px] pb-[74px] min-[1024px]:hidden",
        )}
      >
        {hasImage ? (
          <div className="flex h-auto w-auto items-center justify-center">
            <Image
              src={src}
              alt={certificate_image_alt || "Section image"}
              width={410}
              height={372}
              className="!h-[248px] !w-[314px] max-w-full object-contain"
              sizes="314px"
            />
          </div>
        ) : null}
        <div
          className={cn(
            "mt-14 flex w-full items-center justify-center text-center",
            !hasImage && "mt-0",
          )}
        >
          <HeadlineCopy
            title={title}
            titleColor={title_text_color}
            subTitle={sub_title}
            subColor={sub_title_text_color}
            className="text-[46px]"
          />
        </div>
      </div>

      {/* Desktop: row; headline column uses flex centering + text-left like reference */}
      <div
        className={cn(
          "hidden w-full items-center justify-center px-[50px] pt-[60px] pb-[74px] min-[1024px]:flex",
        )}
      >
        {hasImage ? (
          <div className="ml-[50px] flex h-[372px] w-[410px] shrink-0 items-center justify-center">
            <Image
              src={src}
              alt={certificate_image_alt || "Section image"}
              width={410}
              height={372}
              className="!h-[372px] !w-[410px] max-w-full object-contain"
              sizes="410px"
            />
          </div>
        ) : null}
        <div
          className={cn(
            "flex w-[314px] shrink-0 items-center justify-center text-left",
            hasImage ? "ml-[154px] mr-[50px]" : "mx-auto",
          )}
        >
          <HeadlineCopy
            title={title}
            titleColor={title_text_color}
            subTitle={sub_title}
            subColor={sub_title_text_color}
            className="text-[52px]"
          />
        </div>
      </div>
    </section>
  );
}

export default CareerProspects3;
